// components/Sections/Admin/Matriculas/Funcionarios.jsx
//
// Nómina de funcionarios: quien está acá paga el valor "funcionario" de la
// matrícula (web y caja), si el recinto tiene ese valor configurado. Se busca por
// el RUT de la persona inscrita.
//
// Se mantiene a mano o subiendo un Excel (RUT y nombre). Quitar a alguien no le
// quita la matrícula que ya pagó; solo deja de tener el precio para compras nuevas.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Form, Input, Modal, Popconfirm, Table } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { FiDownload, FiPlus, FiSearch, FiTrash2, FiUploadCloud } from 'react-icons/fi';
import dayjs from 'dayjs';
import MatriculaService from '../../../../service/Matriculas';
import { useNotify } from '../../../../providers/NotificationProvider';
import { descargarPlantilla, leerPlanilla } from '../../../../utils/plantillaExcel';
import { EncabezadoAdmin, ChipNeutro, claseBtnPrimario, claseBtnSecundario } from '../../../Ui/Admin/PiezasAdmin';

const mensaje = (e, porDefecto) => e?.error || e?.message || porDefecto;

const COLUMNAS = [
  { header: 'RUT', key: 'rut', requerida: true, ancho: 16, ejemplo: '12.345.678-5' },
  { header: 'Nombre', key: 'nombre', ancho: 36, ejemplo: 'María Pérez Soto' },
];

const Funcionarios = () => {
  const notify = useNotify();
  const [form] = Form.useForm();
  const inputRef = useRef(null);

  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [buscar, setBuscar] = useState('');
  const [modalAgregar, setModalAgregar] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [quitando, setQuitando] = useState(null);
  const [carga, setCarga] = useState(null); // { filas, vista }
  const [procesando, setProcesando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setLista(await MatriculaService.ListarFuncionarios({ buscar: buscar.trim() || undefined }));
    } catch (e) {
      notify.error(mensaje(e, 'No se pudo cargar la nómina'));
    } finally {
      setCargando(false);
    }
  }, [buscar, notify]);

  useEffect(() => {
    const t = setTimeout(cargar, 300);
    return () => clearTimeout(t);
  }, [cargar]);

  const agregar = async (values) => {
    setGuardando(true);
    try {
      await MatriculaService.AgregarFuncionario(values);
      notify.success('Funcionario agregado');
      setModalAgregar(false);
      form.resetFields();
      cargar();
    } catch (e) {
      notify.error(mensaje(e, 'No se pudo agregar'));
    } finally {
      setGuardando(false);
    }
  };

  const quitar = async (f) => {
    setQuitando(f._id);
    try {
      await MatriculaService.QuitarFuncionario(f._id);
      notify.success('Quitado de la nómina');
      cargar();
    } catch (e) {
      notify.error(mensaje(e, 'No se pudo quitar'));
    } finally {
      setQuitando(null);
    }
  };

  const alSubir = async (e) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = '';
    if (!file) return;
    setProcesando(true);
    try {
      const { filas, faltantes } = await leerPlanilla(file, COLUMNAS);
      if (faltantes.length) throw new Error(`Al archivo le faltan las columnas: ${faltantes.join(', ')}`);
      if (!filas.length) throw new Error('El archivo no trae filas con datos');
      setCarga({ nombre: file.name, filas, vista: await MatriculaService.CargaMasivaFuncionarios(filas, false) });
    } catch (err) {
      notify.error(mensaje(err, 'No se pudo leer el archivo'));
    } finally {
      setProcesando(false);
    }
  };

  const aplicarCarga = async () => {
    setProcesando(true);
    try {
      const r = await MatriculaService.CargaMasivaFuncionarios(carga.filas, true);
      notify.success(`Nómina actualizada: ${r.resumen.nuevos} nuevos, ${r.resumen.reactivados} reactivados`);
      setCarga(null);
      cargar();
    } catch (err) {
      notify.error(mensaje(err, 'No se pudo aplicar la carga'));
    } finally {
      setProcesando(false);
    }
  };

  const columnas = [
    { title: 'RUT', dataIndex: 'rut', width: 140, render: r => <span className="tabular-nums font-bold text-gray-900">{r}</span> },
    { title: 'Nombre', dataIndex: 'nombre', render: n => n || '—' },
    { title: 'Origen', dataIndex: 'origen', width: 140, render: o => <ChipNeutro>{o === 'carga_masiva' ? 'Carga masiva' : 'Manual'}</ChipNeutro> },
    { title: 'Desde', dataIndex: 'createdAt', width: 120, render: d => (d ? dayjs(d).format('DD/MM/YYYY') : '—') },
    {
      title: <span className="sr-only">Acciones</span>, key: 'acc', width: 120, align: 'right',
      render: (_, f) => (
        <Popconfirm title="¿Quitar de la nómina?" description="Deja de tener el precio funcionario en compras nuevas."
          okText="Sí, quitar" cancelText="No" okButtonProps={{ danger: true }} onConfirm={() => quitar(f)}>
          <button type="button" disabled={quitando === f._id}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 text-xs font-bold text-gray-500 hover:border-red-300 hover:text-red-600 transition-colors disabled:opacity-60">
            {quitando === f._id ? <LoadingOutlined /> : <FiTrash2 size={12} />} Quitar
          </button>
        </Popconfirm>
      ),
    },
  ];

  const vista = carga?.vista;

  return (
    <div className="max-w-[1100px]">
      <EncabezadoAdmin titulo="Nómina de" destacado="funcionarios"
        subtitulo="Las personas de esta lista pagan el valor funcionario de la matrícula">
        <button type="button" onClick={() => descargarPlantilla({ archivo: 'plantilla-funcionarios', columnas: COLUMNAS })} className={claseBtnSecundario}>
          <FiDownload size={15} /> Plantilla
        </button>
        <input ref={inputRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={alSubir} />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={procesando} className={claseBtnSecundario}>
          {procesando ? <LoadingOutlined /> : <FiUploadCloud size={15} />} Subir Excel
        </button>
        <button type="button" onClick={() => setModalAgregar(true)} className={claseBtnPrimario}>
          <FiPlus size={16} /> Agregar
        </button>
      </EncabezadoAdmin>

      <div className="flex items-center gap-3 p-3 mb-4 rounded-2xl bg-white border border-gray-100">
        <Input allowClear prefix={<FiSearch className="text-gray-400" />} placeholder="Buscar por RUT o nombre"
          value={buscar} onChange={e => setBuscar(e.target.value)} className="max-w-[360px]" />
        <span className="ml-auto text-xs text-gray-500">{lista.length} funcionario{lista.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="admin-table-card">
        <Table columns={columnas} dataSource={lista} rowKey="_id" loading={cargando} scroll={{ x: 640 }}
          pagination={lista.length > 50 ? { pageSize: 50, style: { padding: '12px 16px' } } : false}
          locale={{ emptyText: <div className="py-10 text-center text-sm text-gray-500">{buscar ? 'Nadie coincide con la búsqueda' : 'La nómina está vacía'}</div> }} />
      </div>

      <Modal open={modalAgregar} onCancel={() => setModalAgregar(false)} title="Agregar funcionario" width={440}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setModalAgregar(false)} className={claseBtnSecundario}>Cancelar</button>
            <button type="button" onClick={() => form.submit()} disabled={guardando} className={claseBtnPrimario}>
              {guardando && <LoadingOutlined />} Agregar
            </button>
          </div>
        }>
        <Form form={form} layout="vertical" onFinish={agregar} autoComplete="off" className="form-compacto">
          <Form.Item name="rut" label="RUT" rules={[{ required: true, message: 'Ingresa el RUT' }]}>
            <Input placeholder="12.345.678-5" />
          </Form.Item>
          <Form.Item name="nombre" label="Nombre" className="!mb-0">
            <Input placeholder="Nombre completo" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal open={!!carga} onCancel={() => setCarga(null)} width={640}
        title={`Vista previa · ${carga?.nombre || ''}`}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setCarga(null)} className={claseBtnSecundario}>Cancelar</button>
            <button type="button" onClick={aplicarCarga} disabled={procesando || !(vista?.resumen.nuevos + vista?.resumen.reactivados)} className={claseBtnPrimario}>
              {procesando && <LoadingOutlined />} Aplicar
            </button>
          </div>
        }>
        {vista && (
          <>
            <Alert type="info" showIcon className="mb-3"
              message={`${vista.resumen.nuevos} nuevos · ${vista.resumen.reactivados} reactivados · ${vista.resumen.yaExistian} ya estaban · ${vista.resumen.errores + vista.resumen.omitidas} con error u omitidos`} />
            <Table size="small" rowKey="fila" dataSource={vista.filas.filter(f => f.estado !== 'ok' || f.accion !== 'ya_existe')}
              pagination={{ pageSize: 10 }}
              columns={[
                { title: 'Fila', dataIndex: 'fila', width: 60 },
                { title: 'RUT', dataIndex: 'rut', width: 130 },
                { title: 'Nombre', dataIndex: 'nombre' },
                {
                  title: 'Resultado', key: 'r', width: 170,
                  render: (_, f) => f.estado === 'ok'
                    ? (f.accion === 'nuevo' ? 'Se agrega' : 'Se reactiva')
                    : <span className="text-red-600">{f.motivo}</span>,
                },
              ]} />
          </>
        )}
      </Modal>
    </div>
  );
};

export default Funcionarios;
